/**
 * Shared OCS request builders + response parsers.
 *
 * Console, REST API, MCP, and CLI MUST call these instead of inventing
 * per-surface shapes. Live eSIMVault OCS is picky:
 *   - getSubscriberLocation takes a flat SubscriberId ({ iccid }), NOT
 *     { subscriber: { iccid } } (OCS error 2 "Unrecognized field subscriber").
 *   - subscriberUsageOverPeriod takes { subscriber: { iccid }, period }
 *     and a max inclusive window of 7 days. Wider windows 400/empty.
 *   - Usage bytes live in quantityPerType["33"]; daily rollup may sit at
 *     data.subsPeriodUsages OR data.usages[0].subsPeriodUsages.
 */

export const OCS_MAX_USAGE_WINDOW_DAYS = 7;
export const OCS_DATA_USAGE_TYPE = "33";

export function clampUsagePeriod(
  start: string,
  end: string,
  maxDays = OCS_MAX_USAGE_WINDOW_DAYS,
): { start: string; end: string } {
  const endMs = Date.parse(`${end}T00:00:00Z`);
  const startMs = Date.parse(`${start}T00:00:00Z`);
  if (Number.isNaN(endMs) || Number.isNaN(startMs) || startMs > endMs) {
    return { start, end };
  }
  const minStartMs = endMs - (maxDays - 1) * 86_400_000;
  if (startMs < minStartMs) {
    return { start: new Date(minStartMs).toISOString().slice(0, 10), end };
  }
  return { start, end };
}

/** Inclusive last-N-days window ending today (UTC). days=7 → start = today-6. */
export function lastNDaysPeriod(days = OCS_MAX_USAGE_WINDOW_DAYS, now = new Date()): { start: string; end: string } {
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const start = new Date(end);
  start.setUTCDate(end.getUTCDate() - (days - 1));
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

export function locationParams(iccid: string): { iccid: string } {
  return { iccid };
}

/** Flat SubscriberId — same shape as location. Active period rejects a nested subscriber wrapper. */
export function subscriberIdParams(iccid: string): { iccid: string } {
  return { iccid };
}

export interface RecurringPackageOptions {
  activationAtFirstUse?: boolean;
  startTimeUTC?: string;
}

export function recurringPackageParams(
  iccid: string,
  packageTemplateId: number,
  options: RecurringPackageOptions = {},
): Record<string, unknown> {
  if (options.activationAtFirstUse !== undefined && options.startTimeUTC !== undefined) {
    throw new Error("activationAtFirstUse cannot be combined with startTimeUTC");
  }
  return {
    subscriber: { iccid },
    packageTemplateId,
    ...(options.startTimeUTC !== undefined
      ? { startTimeUTC: options.startTimeUTC }
      : { activationAtFirstUse: options.activationAtFirstUse ?? true }),
  };
}

export function networkEventsOverPeriodParams(
  iccid: string,
  start: string,
  end: string,
): { subscriber: { iccid: string }; period: { start: string; end: string } } {
  return usageOverPeriodParams(iccid, start, end);
}

export type OcsRadioType = "2G" | "3G" | "4G" | "5G" | "NB-IoT";

export function radioTypeFromRat(rat: unknown): OcsRadioType | undefined {
  const s = String(rat ?? "").toUpperCase();
  if (!s) return undefined;
  if (s.includes("NB")) return "NB-IoT";
  if (s.includes("5G") || s.includes("NR")) return "5G";
  if (s.includes("4G") || s.includes("LTE")) return "4G";
  if (s.includes("3G") || s.includes("UMTS") || s.includes("HSPA") || s.includes("WCDMA")) return "3G";
  if (s.includes("2G") || s.includes("GSM") || s.includes("GPRS") || s.includes("EDGE")) return "2G";
  return undefined;
}

export interface CellTuple {
  radioType: OcsRadioType;
  mcc: number;
  mnc: number;
  lac: number;
  cellId?: number;
}

export function cellTupleFromNetworkInfo(networkInfo: unknown): CellTuple | undefined {
  if (!networkInfo || typeof networkInfo !== "object") return undefined;
  const rec = networkInfo as Record<string, unknown>;
  const radioType = radioTypeFromRat(rec.lastRat ?? rec.rat ?? rec.radioType);
  const mcc = Number(rec.lastMcc ?? rec.mcc);
  const mnc = Number(rec.lastMnc ?? rec.mnc);
  const lac = Number(rec.lastLac ?? rec.lac);
  const cellIdRaw = rec.lastCellId ?? rec.cellId;
  if (!radioType || !Number.isFinite(mcc) || !Number.isFinite(mnc) || !Number.isFinite(lac)) return undefined;
  const out: CellTuple = { radioType, mcc, mnc, lac };
  const cellId = Number(cellIdRaw);
  if (Number.isFinite(cellId) && cellId > 0) out.cellId = cellId;
  return out;
}

export function cellLocationParams(cell: CellTuple): {
  radioType: OcsRadioType;
  mcc: number;
  mnc: number;
  lac: number;
  cellId?: number;
} {
  const params: { radioType: OcsRadioType; mcc: number; mnc: number; lac: number; cellId?: number } = {
    radioType: cell.radioType,
    mcc: cell.mcc,
    mnc: cell.mnc,
    lac: cell.lac,
  };
  if (cell.cellId != null) params.cellId = cell.cellId;
  return params;
}

export function usageOverPeriodParams(
  iccid: string,
  start: string,
  end: string,
): { subscriber: { iccid: string }; period: { start: string; end: string } } {
  const period = clampUsagePeriod(start, end);
  return { subscriber: { iccid }, period };
}

/**
 * Pull the IMSI out of a `getSingleSubscriber` record.
 *
 * Live OCS returns the IMSI under `imsiList[]` (and `multiImsi[]`), never as a
 * bare top-level `imsi` property. Reading `record.imsi` directly is always
 * `undefined`, which is why every HLR/SMS tool that needed an IMSI failed with
 * "Could not resolve IMSI for ICCID <n>" against a perfectly healthy SIM.
 *
 * Verified 2026-08-19 on ICCID 8948010000020241113: `imsiList[0].imsi` =
 * 260010189628101, and `hlrGetBitrate { imsi }` then answers UNLIMITED.
 *
 * Accepts either the unwrapped record or the `{ getSingleSubscriber: {...} }`
 * envelope, since call sites differ on which one they hold.
 */
export function imsiFromSubscriberRecord(record: unknown): string | undefined {
  if (!record || typeof record !== "object") return undefined;
  const rec = record as Record<string, unknown>;
  const inner =
    rec.getSingleSubscriber && typeof rec.getSingleSubscriber === "object"
      ? (rec.getSingleSubscriber as Record<string, unknown>)
      : rec;

  const direct = inner.imsi;
  if (typeof direct === "string" && direct.length > 0) return direct;

  for (const key of ["imsiList", "multiImsi"] as const) {
    const list = inner[key];
    if (!Array.isArray(list)) continue;
    for (const entry of list) {
      if (!entry || typeof entry !== "object") continue;
      const value = (entry as Record<string, unknown>).imsi;
      if (typeof value === "string" && value.length > 0) return value;
    }
  }
  return undefined;
}


/**
 * `subscriberId` on modifySubscriberMobilePlan / modifySubscriberVoipPlan is a
 * SubscriberId OBJECT, not a bare id.
 *
 * The name reads like a number and that is exactly the trap. Probed live
 * 2026-08-28:
 *
 *   {"modifySubscriberMobilePlan":{"subscriberId":{"__carrierParamProbe__":1}}}
 *     -> Unrecognized field ... (class srv.ocs.backend.lib.json.api.common.SubscriberId),
 *        6 known properties: "multiImsi", "iccid", "imsi", "activationCode",
 *        "msisdn", "subscriberId"
 *
 * That is the same common.SubscriberId class cleanSubscriberAllPackages and
 * listSubscriberPrepaidPackages take, so an ICCID identifies the subscriber
 * directly and NO id lookup is needed. Bridge4IP documents the same shape
 * (docs/research/bridge4ip-ocs-api/endpoints-full.md §3.10/3.11:
 * `{"subscriberId": {"subscriberId": 1000}, "mobilePlanId": 10}`) and calls
 * the doubled key a quirk of their own docs.
 *
 * Writing the ICCID as a BARE `subscriberId` would be the dangerous fix: the
 * inner `subscriberId` is an internal numeric id in a different id space, so a
 * 20-digit ICCID landing there could address another record entirely. Nesting
 * keeps the identifier in the field that means ICCID.
 */
export function subscriberPlanParams(
  iccid: string,
): { subscriberId: { iccid: string } } {
  return { subscriberId: { iccid } };
}

/**
 * Limit fields OCS accepts inside `modifySubscriberPrepaidPackageLimits`'s
 * `newLimits` object, mapped from the names callers already use.
 *
 * Probed live 2026-08-28 by pointing the probe sentinel at the SUB-object —
 * the same Jackson trick works one level down:
 *
 *   {"modifySubscriberPrepaidPackageLimits":{"newLimits":{"__carrierParamProbe__":1}}}
 *     -> Unrecognized field ... (class Request$SubscriberPrepaidPackageLimits),
 *        5 known properties: "mtcSecond", "dataByte", "mocSecond", "moSms", "mtSms"
 *
 * Matches Bridge4IP's documented example exactly (endpoints-full.md §3.4).
 * The tool's own description advertises `dataLimit`, so the aliases are the
 * published contract and are mapped rather than broken.
 */
export const OCS_PREPAID_PACKAGE_LIMIT_FIELDS = [
  "dataByte",
  "mocSecond",
  "mtcSecond",
  "moSms",
  "mtSms",
] as const;

const LIMIT_ALIASES: Record<string, (typeof OCS_PREPAID_PACKAGE_LIMIT_FIELDS)[number]> = {
  dataByte: "dataByte",
  dataBytes: "dataByte",
  dataLimit: "dataByte",
  databyte: "dataByte",
  mocSecond: "mocSecond",
  mocSeconds: "mocSecond",
  voiceOutLimit: "mocSecond",
  mtcSecond: "mtcSecond",
  mtcSeconds: "mtcSecond",
  voiceInLimit: "mtcSecond",
  moSms: "moSms",
  moSmsNumber: "moSms",
  smsOutLimit: "moSms",
  mtSms: "mtSms",
  mtSmsNumber: "mtSms",
  smsInLimit: "mtSms",
};

/**
 * Build the `newLimits` sub-object.
 *
 * Unknown keys THROW rather than being dropped. Silently discarding a limit a
 * caller asked for would report success on a package whose allowance never
 * changed, which is worse than the rejection this replaces: today OCS refuses
 * the whole call, so nothing is written and nothing is claimed.
 *
 * `null` is preserved — OCS reads it as "leave this limit unchanged".
 */
export function prepaidPackageLimits(
  limits: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const unknown: string[] = [];
  for (const [key, value] of Object.entries(limits)) {
    const target = LIMIT_ALIASES[key];
    if (!target) {
      unknown.push(key);
      continue;
    }
    out[target] = value;
  }
  if (unknown.length > 0) {
    throw new Error(
      `Unknown package limit field(s): ${unknown.join(", ")}. ` +
        `OCS accepts only ${OCS_PREPAID_PACKAGE_LIMIT_FIELDS.join(", ")} ` +
        `inside newLimits (aliases: ${Object.keys(LIMIT_ALIASES).join(", ")}).`,
    );
  }
  if (Object.keys(out).length === 0) {
    throw new Error(
      `No package limits to change. Provide at least one of ` +
        `${OCS_PREPAID_PACKAGE_LIMIT_FIELDS.join(", ")}.`,
    );
  }
  return out;
}

/**
 * Coerce a caller's date into the `java.time.LocalDateTime` OCS parses.
 *
 * `newExpirationDate` is a LocalDateTime and a date-only string is REJECTED —
 * verified live 2026-08-28 against packageId -1 (a row that cannot exist, so
 * the parse verdict is the only thing observed):
 *
 *   "2026-06-01"           -> code 2, DateTimeParseException at index 10
 *   "2026-06-01T23:59:59"  -> code 6, "Subscriber package not found"
 *
 * Code 6 means the payload parsed and business logic ran, which is the pass.
 *
 * `bareDate` decides what a date-only string means, and the two answers are
 * not interchangeable. An EXPIRY widened to 00:00:00 silently cuts the
 * caller's last day off the package, so expiries end the day. An ACTIVATION
 * pushed to 23:59:59 silently costs the caller the whole first day, so
 * activations start it. Defaulting to "end" keeps every existing expiry call
 * site behaving exactly as before.
 */
export function ocsLocalDateTime(value: string, bareDate: "start" | "end" = "end"): string {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return bareDate === "start" ? `${trimmed}T00:00:00` : `${trimmed}T23:59:59`;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)) return `${trimmed}:00`;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(trimmed)) {
    // Strip a trailing zone designator: LocalDateTime has no offset and OCS
    // throws on one.
    return trimmed.replace(/(?:Z|[+-]\d{2}:?\d{2})$/, "").slice(0, 19);
  }
  throw new Error(
    `"${value}" is not a date OCS can read. Use YYYY-MM-DD or ` +
      `YYYY-MM-DDTHH:mm:ss — this field is a java.time.LocalDateTime and a ` +
      `zone offset is rejected.`,
  );
}

/**
 * The active-period window OCS currently holds for one subscriber package.
 *
 * Read from a `listSubscriberPrepaidPackages` response, whose rows carry the
 * live window as `tsactivationutc` / `tsexpirationutc` — already in the
 * `YYYY-MM-DDTHH:mm:ss` LocalDateTime form the write side wants, so the values
 * round-trip without reformatting.
 *
 * Verified live 2026-08-28 on ICCID 8948010000020241170, package 208596510:
 *   tsactivationutc "2026-08-27T21:15:38", tsexpirationutc "2027-08-27T21:15:38".
 *
 * Returns undefined for a package that is not on this subscriber, so a caller
 * refuses instead of writing a window onto a row it never read.
 */
export function activePeriodFromPackages(
  listResponse: unknown,
  packageId: number,
): { activation?: string; expiration?: string } | undefined {
  const root = (listResponse ?? {}) as Record<string, unknown>;
  const payload = (root.listSubscriberPrepaidPackages ?? root.data ?? root) as
    | Record<string, unknown>
    | undefined;
  const packages = Array.isArray(payload?.packages) ? payload.packages : [];
  for (const entry of packages) {
    if (!entry || typeof entry !== "object") continue;
    const rec = entry as Record<string, unknown>;
    const id = rec.subscriberprepaidpackageid ?? rec.subscriberPrepaidPackageId;
    if (Number(id) !== packageId) continue;
    const activation = rec.tsactivationutc ?? rec.tsActivationUtc;
    const expiration = rec.tsexpirationutc ?? rec.tsExpirationUtc;
    const window: { activation?: string; expiration?: string } = {};
    if (typeof activation === "string" && activation) window.activation = activation;
    if (typeof expiration === "string" && expiration) window.expiration = expiration;
    return window;
  }
  return undefined;
}

/** Sent when a caller supplies no comment. OCS requires the field to be present. */
export const OCS_ACTIVE_PERIOD_DEFAULT_COMMENT = "Active period changed via Carrier";

export interface ActivePeriodChange {
  /** New start of the window. Bare dates mean 00:00:00. */
  startDate?: string;
  /** New end of the window. Bare dates mean 23:59:59. */
  endDate?: string;
  /** Audit note stored on the OCS record. */
  comment?: string;
  /** The window OCS holds today, used to fill whichever bound the caller omitted. */
  current?: { activation?: string; expiration?: string };
}

/**
 * Build `modifySubscriberPrepaidPackageActivePeriod` params.
 *
 * OCS accepts exactly four field names here and REQUIRES all four. Every other
 * shape fails the whole request, which is how this route 502'd on every call
 * it ever served. Verified live 2026-08-28 against ocs.esimvault.cloud:
 *
 *   {subscriber, packageId, startDate, endDate} -> code 2, Unrecognized field "subscriber"
 *   {packageId, startDate, endDate}             -> code 2, Unrecognized field "startDate"
 *   {packageId, newActivationDateUtc}           -> code 2, Missing 'newExpirationDateUtc'
 *   {packageId, newExpirationDateUtc}           -> code 2, Missing 'newActivationDateUtc'
 *   {packageId, both dates}                     -> code 2, Missing 'comment'
 *   {packageId, both dates, comment}            -> code 6, Subscriber package not found
 *
 * Code 6 is the pass: the payload parsed and OCS ran the lookup. Note the
 * third and fourth lines — "both dates are mandatory" is why a partial change
 * cannot simply omit a bound. The caller must read the live window and pass it
 * as `current`, or this throws rather than inventing one.
 *
 * `packageId` is the SUBSCRIBER package id (`subscriberprepaidpackageid`), not
 * the template id. Verified live on ICCID 8948010000020241170: template
 * 2895241 answers code 6 "Subscriber package not found", subscriber package
 * 208596510 answers code 14 "Can only modify package configured to be
 * activated at first use" — a business rule, so that id reached the row.
 */
export function packageActivePeriodParams(
  packageId: number,
  change: ActivePeriodChange,
): {
  packageId: number;
  newActivationDateUtc: string;
  newExpirationDateUtc: string;
  comment: string;
} {
  if (change.startDate === undefined && change.endDate === undefined) {
    throw new Error("Provide startDate and/or endDate to change a package's active period.");
  }
  const activation =
    change.startDate !== undefined
      ? ocsLocalDateTime(change.startDate, "start")
      : change.current?.activation;
  const expiration =
    change.endDate !== undefined
      ? ocsLocalDateTime(change.endDate, "end")
      : change.current?.expiration;

  for (const [field, value, omitted] of [
    ["newActivationDateUtc", activation, "startDate"],
    ["newExpirationDateUtc", expiration, "endDate"],
  ] as const) {
    if (value !== undefined) continue;
    throw new Error(
      `OCS requires both bounds on an active-period change and rejects the ` +
        `whole request with "Missing '${field}'" when one is absent. ` +
        `Supply ${omitted}, or read the package's current window first ` +
        `(activePeriodFromPackages) and pass it as \`current\`.`,
    );
  }

  return {
    packageId,
    // Already LocalDateTime-shaped when it came from `current`; normalising
    // again is a no-op and keeps a hand-passed value honest.
    newActivationDateUtc: ocsLocalDateTime(activation as string, "start"),
    newExpirationDateUtc: ocsLocalDateTime(expiration as string, "end"),
    comment: change.comment?.trim() || OCS_ACTIVE_PERIOD_DEFAULT_COMMENT,
  };
}


/**
 * Resolve a subscriber package id to the recurring subscription that produced
 * it, for `stopResumeSubsRecurringPackage`.
 *
 * These are three DIFFERENT id spaces, which is why a rename was never the
 * fix. One live package on ICCID 8948010000020241105 (2026-08-28):
 *
 *   subscriberprepaidpackageid  204532344   <- what list_subscriber_packages returns
 *   recurringPackage             11683296   <- what OCS wants as `recurringId`
 *   templateId                    3848928   <- stops EVERY subscriber on the template
 *
 * Passing the package id as `recurringId` would address an unrelated recurring
 * subscription; passing it as `templateId` would stop the template fleet-wide.
 *
 * Returns undefined when the package is not on the subscriber or carries no
 * `recurringPackage` (a one-off package has none), so the caller can refuse
 * instead of guessing.
 */
export function recurringIdForPackage(
  listResponse: unknown,
  packageId: number,
): number | undefined {
  const root = (listResponse ?? {}) as Record<string, unknown>;
  const payload = (root.listSubscriberPrepaidPackages ?? root.data ?? root) as
    | Record<string, unknown>
    | undefined;
  const packages = Array.isArray(payload?.packages) ? payload.packages : [];
  for (const entry of packages) {
    if (!entry || typeof entry !== "object") continue;
    const rec = entry as Record<string, unknown>;
    const id = rec.subscriberprepaidpackageid ?? rec.subscriberPrepaidPackageId;
    if (Number(id) !== packageId) continue;
    const recurring = rec.recurringPackage ?? rec.recurringId;
    const value = Number(recurring);
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }
  return undefined;
}

export interface UsageDayPoint {
  date: string;
  mb: number;
}

export interface UsageCountrySlice {
  country: string;
  mb: number;
  operators: { name: string; mb: number }[];
}

export function mbFromBytes(bytes: number | undefined): number {
  if (!bytes) return 0;
  return Math.round(bytes / 1_048_576);
}

export function dataBytesFromQuantity(qty: unknown): number {
  if (!qty || typeof qty !== "object") return 0;
  const rec = qty as Record<string, unknown>;
  const raw = rec[OCS_DATA_USAGE_TYPE] ?? rec[33];
  return typeof raw === "number" ? raw : 0;
}

function periodRows(data: Record<string, unknown> | undefined): unknown[] {
  if (!data) return [];
  if (Array.isArray(data.subsPeriodUsages) && data.subsPeriodUsages.length) return data.subsPeriodUsages;
  const usages = Array.isArray(data.usages) ? data.usages : [];
  return usages.flatMap((u) => {
    const rec = u as { subsPeriodUsages?: unknown[] };
    return Array.isArray(rec.subsPeriodUsages) ? rec.subsPeriodUsages : [];
  });
}

/** Accepts the REST `{ data }` envelope or the raw OCS usage object. */
export function extractUsageTimeline(json: unknown): UsageDayPoint[] {
  const root = (json ?? {}) as Record<string, unknown>;
  const data = (root.data ?? root.usage ?? root.subscriberUsageOverPeriod ?? root) as Record<string, unknown>;
  if (Array.isArray(data)) {
    return data
      .map((entry: { date?: string; day?: string; mb?: number; bytes?: number }) => ({
        date: entry.date ?? entry.day ?? "",
        mb: entry.mb ?? mbFromBytes(entry.bytes),
      }))
      .filter((p) => p.date);
  }
  if (!data || typeof data !== "object") return [];

  const periods = periodRows(data);
  if (periods.length > 0) {
    return periods
      .map((p) => {
        const rec = p as { day?: string; date?: string; total?: { quantityPerType?: unknown } };
        return {
          date: rec.day ?? rec.date ?? "",
          mb: mbFromBytes(dataBytesFromQuantity(rec.total?.quantityPerType)),
        };
      })
      .filter((p) => p.date);
  }

  const sessions = Array.isArray(data.usages)
    ? (data.usages as { subsDailyUsages?: unknown[]; usageDateUtc?: string; quantity?: number }[]).flatMap((u) =>
        Array.isArray(u.subsDailyUsages) && u.subsDailyUsages.length ? u.subsDailyUsages : [u],
      )
    : [];
  if (sessions.length === 0) return [];
  const byDay = new Map<string, number>();
  for (const s of sessions) {
    const rec = s as { day?: string; usageDateUtc?: string; quantity?: number; total?: { quantityPerType?: unknown } };
    const day = rec.day ?? (typeof rec.usageDateUtc === "string" ? rec.usageDateUtc.slice(0, 10) : "");
    if (!day) continue;
    const bytes =
      typeof rec.quantity === "number" ? rec.quantity : dataBytesFromQuantity(rec.total?.quantityPerType);
    byDay.set(day, (byDay.get(day) ?? 0) + bytes);
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, bytes]) => ({ date, mb: mbFromBytes(bytes) }));
}

export function extractUsageBreakdown(json: unknown): { totalMb: number; countries: UsageCountrySlice[] } {
  const root = (json ?? {}) as Record<string, unknown>;
  const data = (root.data ?? root.usage ?? root.subscriberUsageOverPeriod ?? root) as Record<string, unknown> | undefined;
  const totalObj =
    data && typeof data === "object"
      ? ((data.total ?? (Array.isArray(data.usages) ? (data.usages[0] as { total?: unknown })?.total : undefined)) as
          | { quantityPerType?: unknown; quantityPerCountry?: unknown[] }
          | undefined)
      : undefined;
  const totalMb = mbFromBytes(dataBytesFromQuantity(totalObj?.quantityPerType));
  const rows = (Array.isArray(totalObj?.quantityPerCountry) ? totalObj.quantityPerCountry : []) as {
    name?: string;
    qty?: number;
    quantityPerOperator?: { name?: string; qty?: number }[];
  }[];
  const countries: UsageCountrySlice[] = rows.map((c) => ({
    country: c.name ?? "Unknown",
    mb: mbFromBytes(typeof c.qty === "number" ? c.qty : 0),
    operators: (c.quantityPerOperator ?? []).map((o) => ({
      name: o.name ?? "Unknown",
      mb: mbFromBytes(typeof o.qty === "number" ? o.qty : 0),
    })),
  }));
  return { totalMb, countries };
}

export function parseSubscriberLocation(json: unknown): {
  latitude: number;
  longitude: number;
  accuracy?: number;
  at?: string;
} | undefined {
  const root = (json ?? {}) as Record<string, unknown>;
  const loc = (root.subscriberLocation ??
    (root.data as Record<string, unknown> | undefined)?.subscriberLocation ??
    root.data ??
    root) as Record<string, unknown> | undefined;
  if (!loc || typeof loc !== "object") return undefined;
  const lat = loc.latitude;
  const lng = loc.longitude;
  if (typeof lat !== "number" || typeof lng !== "number") return undefined;
  const out: { latitude: number; longitude: number; accuracy?: number; at?: string } = {
    latitude: lat,
    longitude: lng,
  };
  if (typeof loc.accuracy === "number") out.accuracy = loc.accuracy;
  const at = typeof loc.dateTime === "string" ? loc.dateTime : typeof loc.at === "string" ? loc.at : undefined;
  if (at) out.at = at;
  return out;
}

export interface NetworkEvent {
  timestamp: string;
  type: string;
  description: string;
  country?: string;
  operator?: string;
  mcc?: number;
  mnc?: number;
  protocol?: string;
}

const NETWORK_EVENT_GROUPS = ["mapAnswer", "s6aAnswer", "gyAnswer", "camelAnswer", "mocAnswer", "mtcAnswer"] as const;

function hasDirectNetworkEvents(rec: Record<string, unknown>): boolean {
  if (Array.isArray(rec.events)) return true;
  return NETWORK_EVENT_GROUPS.some((key) => {
    const group = rec[key];
    return Boolean(
      group && typeof group === "object" && Array.isArray((group as { events?: unknown[] }).events),
    );
  });
}

/**
 * The API returns `{ data: <OCS body> }`. The OCS body nests protocol groups
 * under `subscriberNetworkEventsOverPeriod`. Either wrapper, or both, has to
 * come off before the groups are readable.
 */
function networkEventPayload(json: unknown): unknown {
  let current: unknown = json;
  for (let depth = 0; depth < 4; depth += 1) {
    if (!current || typeof current !== "object" || Array.isArray(current)) return current;
    const rec = current as Record<string, unknown>;
    if (hasDirectNetworkEvents(rec)) return current;
    const nested = rec.subscriberNetworkEventsOverPeriod;
    if (nested && typeof nested === "object") {
      current = nested;
      continue;
    }
    if ("data" in rec) {
      current = rec.data;
      continue;
    }
    return current;
  }
  return current;
}

function flattenNetworkEventGroups(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  const rec = payload as Record<string, unknown>;
  if (Array.isArray(rec.events)) return rec.events;
  const out: unknown[] = [];
  for (const key of NETWORK_EVENT_GROUPS) {
    const group = rec[key];
    if (group && typeof group === "object" && Array.isArray((group as { events?: unknown[] }).events)) {
      out.push(...((group as { events: unknown[] }).events));
    }
  }
  return out;
}

export function extractNetworkEvents(json: unknown): NetworkEvent[] {
  const rows = flattenNetworkEventGroups(networkEventPayload(json));
  return rows
    .map((row) => {
      const rec = (row ?? {}) as Record<string, unknown>;
      const timestamp = String(rec.eventTime ?? rec.timestamp ?? rec.time ?? rec.dateTime ?? "");
      const longCode = typeof rec.longCode === "string" ? rec.longCode : undefined;
      const shortCode = typeof rec.shortCode === "string" ? rec.shortCode : undefined;
      const type = String(rec.eventType ?? rec.type ?? longCode ?? shortCode ?? "network");
      const country = typeof rec.countryName === "string" ? rec.countryName : typeof rec.country === "string" ? rec.country : undefined;
      const operator = typeof rec.operator === "string" ? rec.operator : undefined;
      const mcc = typeof rec.mcc === "number" ? rec.mcc : undefined;
      const mnc = typeof rec.mnc === "number" ? rec.mnc : undefined;
      const protocol = typeof rec.protocol === "string" ? rec.protocol : undefined;
      const result = typeof rec.result === "string" ? rec.result : undefined;
      const parts = [type, operator, country, result].filter(Boolean);
      const event: NetworkEvent = {
        timestamp,
        type,
        description: parts.join(" · ") || "Network event",
      };
      if (country) event.country = country;
      if (operator) event.operator = operator;
      if (mcc != null) event.mcc = mcc;
      if (mnc != null) event.mnc = mnc;
      if (protocol) event.protocol = protocol;
      return event;
    })
    .filter((e) => e.timestamp)
    .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}

export function parseActivePeriod(json: unknown): { start?: string; end?: string } | undefined {
  const root = (json ?? {}) as Record<string, unknown>;
  const payload = (root.getSubscriberActivePeriod ?? root.data ?? root) as Record<string, unknown>;
  const period = (payload.period ?? payload) as Record<string, unknown>;
  const start = typeof period.start === "string" ? period.start : undefined;
  const end = typeof period.end === "string" ? period.end : undefined;
  if (!start && !end) return undefined;
  const out: { start?: string; end?: string } = {};
  if (start) out.start = start;
  if (end) out.end = end;
  return out;
}

export interface EsimStatusCounts {
  active: number;
  suspended: number;
  inventory: number;
  other: number;
  total: number;
}

function emptyEsimStatusCounts(): EsimStatusCounts {
  return { active: 0, suspended: 0, inventory: 0, other: 0, total: 0 };
}

function addEsimStatusRow(acc: EsimStatusCounts, rec: Record<string, unknown>): void {
  const count = Number(rec.count ?? 0);
  if (!Number.isFinite(count) || count === 0) return;
  // Live OCS (account 5259, 2026-08-18): statusNum 0=Free, 2=Affected.
  // Do not trust statusNum 2 as Suspended. Prefer statusStr when present.
  const str = String(rec.statusStr ?? rec.name ?? rec.status ?? "").toUpperCase();
  if (str === "ACTIVATED" || str === "ACTIVE" || str === "AFFECTED") {
    acc.active += count;
    return;
  }
  if (str === "SUSPENDED") {
    acc.suspended += count;
    return;
  }
  if (str === "FREE" || str === "INVENTORY" || str === "NOT_ACTIVATED" || str === "AVAILABLE") {
    acc.inventory += count;
    return;
  }
  const num = rec.statusNum;
  if (typeof num === "number") {
    if (num === 1) acc.active += count;
    else if (num === 0) acc.inventory += count;
    else if (num === 2) acc.suspended += count;
    else acc.other += count;
    return;
  }
  acc.other += count;
}

/**
 * Unwrap esimStatusPerAccount.
 * Live shape: { account: [{ sponsor: [{ esim: { status: [{ statusNum, statusStr, count }] } }] }] }
 * statusNum: 0 Free / 1 Activated / 2 Suspended / 3+ other.
 */
export function extractEsimStatusCounts(json: unknown): EsimStatusCounts {
  const acc = emptyEsimStatusCounts();
  const root = (json ?? {}) as Record<string, unknown>;
  const payload = (root.data ?? root.esimStatusPerAccount ?? root) as Record<string, unknown> | unknown[];
  const accounts: unknown[] = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as Record<string, unknown>).account)
      ? ((payload as Record<string, unknown>).account as unknown[])
      : [];

  for (const account of accounts) {
    const rec = (account ?? {}) as Record<string, unknown>;
    const sponsors = Array.isArray(rec.sponsor) ? rec.sponsor : [];
    if (sponsors.length > 0) {
      for (const sp of sponsors) {
        const esim = ((sp as Record<string, unknown>)?.esim ?? {}) as Record<string, unknown>;
        const statuses = Array.isArray(esim.status) ? esim.status : [];
        for (const row of statuses) addEsimStatusRow(acc, (row ?? {}) as Record<string, unknown>);
      }
      continue;
    }
    acc.active += Number(rec.active ?? 0);
    acc.suspended += Number(rec.suspended ?? 0);
    acc.inventory += Number(rec.inventory ?? rec.notActivated ?? rec.free ?? 0);
    acc.other += Number(rec.other ?? rec.terminated ?? 0);
  }

  acc.total = acc.active + acc.suspended + acc.inventory + acc.other;
  return acc;
}

/**
 * Params for `esimStatusPerAccount`.
 *
 * OCS rejects a payload carrying BOTH keys with error 2
 * ("Cannot provide both 'resellerId' and 'accountId'"), so an explicit
 * accountId must suppress the reseller default rather than accompany it.
 * Verified live 2026-08-19 against account 5259 / reseller 1170.
 *
 * Accepts the historical bare-accountId form as well as the
 * `{ accountId?, resellerId? }` form the MCP/CLI reseller-default path needs.
 */
export function esimStatusPerAccountParams(accountId: number): { accountId: number };
export function esimStatusPerAccountParams(args: {
  accountId: number;
  resellerId?: number;
}): { accountId: number };
export function esimStatusPerAccountParams(args: { resellerId: number }): { resellerId: number };
export function esimStatusPerAccountParams(args: {
  accountId?: number;
  resellerId?: number;
}): { accountId: number } | { resellerId: number };
export function esimStatusPerAccountParams(
  args: number | { accountId?: number; resellerId?: number },
): { accountId: number } | { resellerId: number } {
  if (typeof args === "number") return { accountId: args };
  if (args.accountId !== undefined) return { accountId: args.accountId };
  if (args.resellerId !== undefined) return { resellerId: args.resellerId };
  throw new Error("esimStatusPerAccountParams needs an accountId or a resellerId");
}

/**
 * Data allowance used to express an "unlimited" package template.
 *
 * OCS has no unlimited flag. Templates were previously created with
 * `databyte: 0`, which reads as unlimited to a human but is a literal zero to
 * the charging engine — and, critically, to the throttling engine.
 *
 * `throttlingThreshold1Perc` is a percentage OF THE BUNDLE and
 * `throttlingThreshold1Limit` is the throttled speed in Kbps. With
 * `databyte: 0` the trigger point is `perc% x 0 = 0`, so an "unlimited, full
 * speed until N, then slow" plan has no computable full-speed window at all,
 * and every storefront that reads `databyte` renders "0 MB".
 *
 * Unlimited is therefore a real, high ceiling and the throttle is configured
 * separately through the throttling fields.
 */
export const UNLIMITED_DATA_BYTES = 100 * 1024 * 1024 * 1024;

/** Bytes per MB, for turning a throttle trigger in MB into a bundle percentage. */
const BYTES_PER_MB = 1_048_576;

/** Field names OCS accepts for the data allowance on a package template. */
const TEMPLATE_DATA_KEYS = ["databyte", "dataBytes", "dataLimit"] as const;

function readTemplateDataBytes(t: Record<string, unknown>): number | undefined {
  for (const key of TEMPLATE_DATA_KEYS) {
    const v = t[key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return undefined;
}

/**
 * Percentage of `totalBytes` that `mb` megabytes represents, clamped to 1..100
 * and rounded, because OCS only accepts whole percentages.
 */
export function throttlePercentFromMb(mb: number, totalBytes = UNLIMITED_DATA_BYTES): number {
  if (!Number.isFinite(mb) || mb <= 0 || totalBytes <= 0) return 100;
  const pct = Math.round(((mb * BYTES_PER_MB) / totalBytes) * 100);
  return Math.min(100, Math.max(1, pct));
}

/**
 * Throttled-speed values OCS accepts for `throttlingThreshold1Limit` /
 * `throttlingThreshold2Limit`, in Kbps.
 *
 * This is a BITRATE enum, the same family as `hlrSetBitrate` (KB_32…KB_102400) —
 * NOT a data allowance. Reading it as megabytes is the mistake that produced the
 * current Mango catalog: the bitrate field was filled in as if it were the
 * bundle, and `databyte` was left at 0. Validating against this list is what
 * catches that inversion at the call site instead of in production.
 */
export const OCS_THROTTLE_KBPS = [
  128, 256, 384, 512, 1024, 3072, 5120, 7680, 10240, 20480, 51200, 102400,
] as const;

/**
 * `hlrSetBitrate` `limit` values. Bridge4IP documents these strings
 * (KB_32 … KB_102400, plus UNLIMITED), and the MCP tool already sends them.
 * A bare Kbps number is not on that model.
 *
 * Wider than {@link OCS_THROTTLE_KBPS}: package-template throttling omits
 * 32, 64, and 2048, and has no unlimited token.
 */
export const OCS_HLR_BITRATE_LIMITS = [
  "KB_32",
  "KB_64",
  "KB_128",
  "KB_256",
  "KB_384",
  "KB_512",
  "KB_1024",
  "KB_2048",
  "KB_3072",
  "KB_5120",
  "KB_7680",
  "KB_10240",
  "KB_20480",
  "KB_51200",
  "KB_102400",
  "UNLIMITED",
] as const;

const OCS_HLR_BITRATE_SET = new Set<string>(OCS_HLR_BITRATE_LIMITS);

/**
 * Map a console Kbps figure or an already-correct token onto `hlrSetBitrate`'s
 * `limit`. Anything else is refused so a free-typed number cannot reach OCS.
 */
export function ocsHlrBitrate(raw: unknown): string | { error: string } {
  const token = hlrBitrateToken(raw);
  if (token !== undefined && OCS_HLR_BITRATE_SET.has(token)) return token;
  return {
    error:
      "limit must be an HLR bitrate in Kbps (32, 64, 128, 256, 384, 512, 1024, 2048, 3072, 5120, 7680, 10240, 20480, 51200, 102400) or UNLIMITED",
  };
}

/**
 * MCP `hlr_set_bitrate` takes bits per second. OCS `limit` is the KB_ enum.
 * 256000 bps is KB_256. 0 is that tool's "remove the cap" and means UNLIMITED.
 * A figure that is not an exact enum step is refused.
 */
export function ocsHlrBitrateFromBps(raw: number): string | { error: string } {
  if (raw === 0) return "UNLIMITED";
  if (!Number.isInteger(raw) || raw < 0 || raw % 1000 !== 0) {
    return {
      error:
        "bitrate must be an HLR speed in bits per second that matches a KB_ level (for example 256000), or 0 for UNLIMITED",
    };
  }
  const mapped = ocsHlrBitrate(raw / 1000);
  if (typeof mapped === "string") return mapped;
  return {
    error:
      "bitrate must be an HLR speed in bits per second that matches a KB_ level (for example 256000), or 0 for UNLIMITED",
  };
}

function hlrBitrateToken(raw: unknown): string | undefined {
  if (typeof raw === "number" && Number.isInteger(raw) && raw > 0) return `KB_${raw}`;
  if (typeof raw !== "string") return undefined;
  const key = raw.trim().toUpperCase().replace(/\s+/g, "_");
  if (key === "UNLIMITED") return "UNLIMITED";
  if (key.startsWith("KB_")) return key;
  if (/^\d+$/.test(key)) return `KB_${key}`;
  return undefined;
}

/**
 * `changeSimStatus` `newStatus` is Affected or Free.
 *
 * Console sends Enable|Disable. MCP sends ENABLED|DISABLED. Both mean the
 * same pair. DELETED is not on this method.
 */
export function ocsSimStatus(raw: string): "Affected" | "Free" | { error: string } {
  const key = raw.trim().toLowerCase();
  if (key === "enable" || key === "enabled" || key === "affected") return "Affected";
  if (key === "disable" || key === "disabled" || key === "free") return "Free";
  if (key === "deleted") {
    return {
      error: "DELETED is not a changeSimStatus value. newStatus must be Enable or Disable",
    };
  }
  return { error: "newStatus must be Enable or Disable" };
}

/**
 * `recurringPeriodicityType` values OCS accepts.
 *
 * Per the vendor docs (docs.esimvault.cloud/ocs-api, createPrepaidPackageTemplate
 * and modifyPPTRecurring): "Recurring periodicity type. Possible values:
 * 0=Daily, 1=Weekly, 2=Monthly."
 *
 * There is no 3. The live Mango v2 templates were created with
 * `recurringPeriodicityType: 3` and an internal comment describing it as
 * "3=MONTHLY", so their recurrence is configured with a value outside the
 * documented range. Verified 2026-08-20: OCS echoes this field back exactly as
 * sent (a template created with 0 reads back as 0), so the 3 is not a
 * read-side encoding difference.
 */
export const OCS_PERIODICITY = { DAILY: 0, WEEKLY: 1, MONTHLY: 2 } as const;

const OCS_PERIODICITY_VALUES = [0, 1, 2] as const;

/**
 * Ceiling multipliers applied to the full-speed window. Each one divides 100
 * exactly, so the resulting trigger percentage is a whole number and OCS never
 * has to round it. Ordered smallest-first: the first that clears the
 * exhaustion check wins, keeping the bundle no larger than it needs to be.
 */
const CEILING_MULTIPLIERS = [20, 25, 50, 100] as const;

const SECONDS_PER_DAY = 86_400;

/**
 * Bytes a subscriber could pull at `kbps` for `periodDays` straight.
 *
 * Network bitrates are decimal (1 kbps = 1000 bits/s), so this deliberately
 * does not use the 1024-based BYTES_PER_MB.
 */
export function throttledBytesPerPeriod(kbps: number, periodDays = 1): number {
  if (!Number.isFinite(kbps) || kbps <= 0) return 0;
  return (kbps * 1000 * SECONDS_PER_DAY * Math.max(1, periodDays)) / 8;
}

/**
 * Pick a bundle ceiling for an unlimited plan.
 *
 * Two things have to hold at once:
 *  1. The full-speed window must be an exact whole percentage of the ceiling,
 *     because that percentage is the only way OCS can express the trigger.
 *  2. The bundle must be unreachable. A plan sold as unlimited must not run out,
 *     so the ceiling has to clear the window PLUS everything a subscriber could
 *     draw at the throttled rate for the rest of the period.
 *
 * Returns the ceiling in bytes and the whole-number trigger percentage.
 */
export function unlimitedCeilingFor(
  windowMb: number,
  throttleKbps?: number,
  periodDays = 1,
): { ceilingBytes: number; percent: number } {
  const windowBytes = windowMb * BYTES_PER_MB;
  const unreachableAbove = windowBytes + throttledBytesPerPeriod(throttleKbps ?? 0, periodDays);

  for (const multiplier of CEILING_MULTIPLIERS) {
    const ceilingBytes = windowBytes * multiplier;
    if (ceilingBytes > unreachableAbove) {
      return { ceilingBytes, percent: Math.round(100 / multiplier) };
    }
  }

  // Even a 100x ceiling could not outrun the throttled rate. Fall back to the
  // widest representable spread rather than silently shipping a bundle the
  // subscriber can drain.
  const last = CEILING_MULTIPLIERS[CEILING_MULTIPLIERS.length - 1]!;
  return { ceilingBytes: windowBytes * last, percent: Math.round(100 / last) };
}

/**
 * Normalize a package-template payload before it goes to OCS.
 *
 * Callers describe the product the way it is sold and the OCS fields are derived
 * from that:
 *
 * - `throttleAfterMb` — the full-speed window, e.g. 500 for "500 MB/day".
 * - `throttleKbps`    — the speed after the window, e.g. 128. Must be an OCS
 *                       bitrate ({@link OCS_THROTTLE_KBPS}).
 * - `periodDays`      — the package period; 1 for a daily bucket.
 *
 * An unlimited template (explicit `unlimited: true`, or a zero/absent allowance)
 * never gets a 0 allowance. Its ceiling is sized so the window lands on a whole
 * trigger percentage AND the bundle cannot be drained even at the throttled rate
 * for the whole period — a plan sold as unlimited must not run out.
 *
 * Returns a new object; the input is not mutated.
 */
export function normalizePackageTemplate(
  template: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...template };

  const unlimitedFlag = out.unlimited === true;
  const current = readTemplateDataBytes(out);
  const isUnlimited = unlimitedFlag || current === undefined || current === 0;

  const throttleAfterMb = typeof out.throttleAfterMb === "number" ? out.throttleAfterMb : undefined;
  const throttleKbps = typeof out.throttleKbps === "number" ? out.throttleKbps : undefined;
  const periodDays = typeof out.perioddays === "number" ? out.perioddays : 1;

  if (throttleKbps !== undefined && !OCS_THROTTLE_KBPS.includes(throttleKbps as never)) {
    throw new Error(
      `throttleKbps ${throttleKbps} is not an OCS bitrate. ` +
        `Allowed: ${OCS_THROTTLE_KBPS.join(", ")}. ` +
        `This field is a SPEED in Kbps, not a data allowance.`,
    );
  }

  const periodicity = out.recurringPeriodicityType;
  if (periodicity !== undefined && !OCS_PERIODICITY_VALUES.includes(periodicity as never)) {
    throw new Error(
      `recurringPeriodicityType ${periodicity} is out of range. ` +
        `Allowed: 0=Daily, 1=Weekly, 2=Monthly. ` +
        `(The live v2 Mango templates use 3, which is not a valid value.)`,
    );
  }

  // A daily or weekly recurring template must say how many packages to grant:
  // OCS only lets nbOccurrence default for MONTHLY templates.
  if (
    out.recurring === true &&
    (periodicity === OCS_PERIODICITY.DAILY || periodicity === OCS_PERIODICITY.WEEKLY) &&
    out.nbOccurrence === undefined
  ) {
    throw new Error(
      "nbOccurrence is required for a daily or weekly recurring template; " +
        "OCS only treats it as optional for monthly templates.",
    );
  }

  if (throttleKbps !== undefined) out.throttlingThreshold1Limit = throttleKbps;
  delete out.throttleKbps;

  if (isUnlimited) {
    let ceiling = UNLIMITED_DATA_BYTES;
    if (throttleAfterMb !== undefined && throttleAfterMb > 0) {
      const sized = unlimitedCeilingFor(throttleAfterMb, throttleKbps, periodDays);
      ceiling = sized.ceilingBytes;
      out.throttlingThreshold1Perc = sized.percent;
    }
    // Write the ceiling to whichever key the caller used, defaulting to the
    // OCS-native `databyte`, so no stale 0 is left behind on a sibling key.
    for (const key of TEMPLATE_DATA_KEYS) {
      if (key in out) out[key] = ceiling;
    }
    if (!TEMPLATE_DATA_KEYS.some((k) => k in out)) out.databyte = ceiling;
  }
  delete out.unlimited;

  if (throttleAfterMb !== undefined) {
    // A finite bundle keeps the plain percentage-of-bundle reading; an unlimited
    // one already had its percentage set alongside the ceiling above.
    if (!isUnlimited) {
      const total = readTemplateDataBytes(out) ?? UNLIMITED_DATA_BYTES;
      out.throttlingThreshold1Perc = throttlePercentFromMb(throttleAfterMb, total);
    }
    out.throttlingActive = out.throttlingActive ?? true;
  }
  delete out.throttleAfterMb;

  return out;
}

/**
 * Normalize a PARTIAL template update (`modify_template_core`).
 *
 * Unlike {@link normalizePackageTemplate} this never injects an allowance the
 * caller did not send — a rename must stay a rename. It only rewrites an
 * allowance that is explicitly present and zero, and expands `throttleAfterMb`.
 *
 * Returns a new object; the input is not mutated.
 */
export function normalizePackageTemplateChanges(
  changes: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...changes };

  const explicitlyUnlimited = out.unlimited === true;
  for (const key of TEMPLATE_DATA_KEYS) {
    if (!(key in out)) continue;
    if (out[key] === 0 || explicitlyUnlimited) out[key] = UNLIMITED_DATA_BYTES;
  }
  if (explicitlyUnlimited && !TEMPLATE_DATA_KEYS.some((k) => k in out)) {
    out.databyte = UNLIMITED_DATA_BYTES;
  }
  delete out.unlimited;

  const throttleAfterMb = out.throttleAfterMb;
  if (typeof throttleAfterMb === "number") {
    // Percentages are relative to the bundle; without one in this payload the
    // unlimited ceiling is the only sane basis.
    const total = readTemplateDataBytes(out) ?? UNLIMITED_DATA_BYTES;
    out.throttlingThreshold1Perc = throttlePercentFromMb(throttleAfterMb, total);
  }
  delete out.throttleAfterMb;

  return out;
}

/**
 * True when a template's allowance is the unlimited sentinel or larger.
 * Storefronts should render these as "Unlimited" plus the throttle terms,
 * never as a raw byte count.
 */
export function isUnlimitedDataBytes(bytes: number | undefined | null): boolean {
  return typeof bytes === "number" && bytes >= UNLIMITED_DATA_BYTES;
}

/**
 * OCS `moveSubscriberRangeToAccount` wire shape.
 *
 * Live OCS (probed 2026-08-24) accepts exactly five fields: destAccount,
 * srcAccountId, rangeType, rangeStart, rangeEnd. REST used to forward
 * toAccountId / iccidStart / iccidEnd, which OCS rejects with code 2
 * ("Unrecognized field toAccountId") — eSIMMY hit that 502 on
 * POST /v1/accounts/{accountId}/move-subscribers. MCP was rebuilt onto
 * this shape on 2026-08-28; REST and Console aliases map through here
 * so a later surface cannot invent a sixth name.
 */
export type MoveSubscriberRangeToAccountParams = {
  srcAccountId: number;
  destAccount: number;
  rangeType: "IMSI" | "ICCID";
  rangeStart: string;
  rangeEnd: string;
};

const RANGE_TYPES = new Set(["IMSI", "ICCID"]);

function positiveAccountId(value: unknown): number | undefined {
  if (typeof value === "number") {
    return Number.isInteger(value) && value > 0 ? value : undefined;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isInteger(n) && n > 0 ? n : undefined;
  }
  return undefined;
}

function rangeToken(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const t = value.trim();
  return t.length > 0 ? t : undefined;
}

/**
 * Map any published alias onto the five OCS fields.
 *
 * REST historically took `{ fromAccountId, iccidStart, iccidEnd }` with the
 * destination in the path. MCP takes `{ srcAccountId, destAccount, rangeType,
 * rangeStart, rangeEnd }`. eSIMMY sent destAccount in the body and the source
 * in the path. All three must produce the same OCS body, and none of
 * toAccountId / iccidStart / iccidEnd / fromAccountId may leave this function.
 */
export function moveSubscriberRangeToAccountParams(input: {
  srcAccountId?: unknown;
  destAccount?: unknown;
  fromAccountId?: unknown;
  toAccountId?: unknown;
  rangeType?: unknown;
  rangeStart?: unknown;
  rangeEnd?: unknown;
  iccidStart?: unknown;
  iccidEnd?: unknown;
  pathAccountId?: unknown;
}): MoveSubscriberRangeToAccountParams | { error: string } {
  const destFromBody = positiveAccountId(input.destAccount ?? input.toAccountId);
  const srcFromBody = positiveAccountId(input.srcAccountId ?? input.fromAccountId);
  const pathId = positiveAccountId(input.pathAccountId);

  const destAccount = destFromBody ?? pathId;
  const srcAccountId =
    srcFromBody ?? (destFromBody !== undefined && pathId !== undefined && pathId !== destFromBody ? pathId : undefined);

  if (srcAccountId === undefined || destAccount === undefined) {
    return { error: "srcAccountId and destAccount required" };
  }
  if (srcAccountId === destAccount) {
    return { error: "srcAccountId and destAccount must differ" };
  }

  const rangeStart = rangeToken(input.rangeStart ?? input.iccidStart);
  const rangeEnd = rangeToken(input.rangeEnd ?? input.iccidEnd);
  if (!rangeStart || !rangeEnd) {
    return { error: "rangeStart and rangeEnd required" };
  }
  if (rangeStart > rangeEnd) {
    return { error: "rangeStart must not be higher than rangeEnd" };
  }

  const rawType =
    typeof input.rangeType === "string" && input.rangeType.trim() !== ""
      ? input.rangeType.trim().toUpperCase()
      : input.iccidStart !== undefined || input.iccidEnd !== undefined
        ? "ICCID"
        : undefined;
  if (!rawType || !RANGE_TYPES.has(rawType)) {
    return { error: "rangeType must be IMSI or ICCID" };
  }

  return {
    srcAccountId,
    destAccount,
    rangeType: rawType as "IMSI" | "ICCID",
    rangeStart,
    rangeEnd,
  };
}

/**
 * `createLocationZone` accepts locationZoneName, tadigList, and networkProfileId.
 *
 * `name` and `tadigs` are aliases. ISO countries and MCC-MNC operators are not
 * TADIG codes, and this function does not invent a TADIG from either of them.
 * An explicit canonical key wins over its alias.
 */
export type LocationZoneCreateParams = {
  locationZoneName: string;
  tadigList: string[];
  networkProfileId: number;
};

const LOCATION_ZONE_KEYS = new Set(["locationZoneName", "name", "tadigList", "tadigs", "networkProfileId"]);

function zoneRecord(
  input: Record<string, unknown> | string,
): { ok: true; body: Record<string, unknown> } | { ok: false; error: string } {
  if (typeof input !== "string") {
    if (input === null || typeof input !== "object" || Array.isArray(input)) {
      return { ok: false, error: "zone must be a JSON object" };
    }
    return { ok: true, body: input };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch {
    return { ok: false, error: "zone must be a JSON object" };
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, error: "zone must be a JSON object" };
  }
  return { ok: true, body: parsed as Record<string, unknown> };
}

function zoneName(raw: unknown): string | { error: string } {
  if (raw === undefined || raw === null || raw === "") return { error: "locationZoneName is required" };
  if (typeof raw !== "string") return { error: "locationZoneName must be a string" };
  const name = raw.trim();
  if (!name) return { error: "locationZoneName is required" };
  return name;
}

function zoneTadigs(raw: unknown): string[] | { error: string } {
  if (raw === undefined || raw === null || raw === "") return { error: "tadigList is required" };
  const parts = typeof raw === "string" ? raw.split(/[\s,]+/) : Array.isArray(raw) ? raw : null;
  if (!parts) return { error: "tadigList must be a list of TADIG codes" };
  const list: string[] = [];
  for (const part of parts) {
    if (typeof part !== "string") return { error: "tadigList must be a list of TADIG codes" };
    const code = part.trim();
    if (code) list.push(code);
  }
  if (list.length === 0) return { error: "tadigList is required" };
  return list;
}

function zoneProfile(raw: unknown): number | undefined {
  if (typeof raw === "number" && Number.isInteger(raw) && raw > 0) return raw;
  if (typeof raw === "string" && /^\d+$/.test(raw.trim())) {
    const n = Number(raw.trim());
    return n > 0 ? n : undefined;
  }
  return undefined;
}

export function locationZoneCreateParams(
  input: Record<string, unknown> | string,
): LocationZoneCreateParams | { error: string } {
  const parsed = zoneRecord(input);
  if (!parsed.ok) return { error: parsed.error };
  const body = parsed.body;
  if ("countries" in body || "operators" in body) {
    return {
      error:
        "createLocationZone has no countries or operators field. Send tadigList (TADIG codes) and networkProfileId.",
    };
  }
  for (const key of Object.keys(body)) {
    if (!LOCATION_ZONE_KEYS.has(key)) {
      return { error: "createLocationZone accepts locationZoneName, tadigList, and networkProfileId" };
    }
  }
  const name = zoneName("locationZoneName" in body ? body.locationZoneName : body.name);
  if (typeof name !== "string") return name;
  const tadigList = zoneTadigs("tadigList" in body ? body.tadigList : body.tadigs);
  if (!Array.isArray(tadigList)) return tadigList;
  const networkProfileId = zoneProfile(body.networkProfileId);
  if (networkProfileId === undefined) {
    return { error: "networkProfileId must be a positive integer" };
  }
  return { locationZoneName: name, tadigList, networkProfileId };
}

/**
 * `createPrepaidPackageTemplate` property names from the live probe.
 * Aliases are mapped onto these. `price` and `description` are not in the set.
 */
export const PACKAGE_TEMPLATE_CREATE_FIELDS = [
  "cost",
  "databyte",
  "deleted",
  "destinationzoneid",
  "esimSponsor",
  "hotspotBlocked",
  "locationzoneid",
  "mocsecond",
  "mosmsnumber",
  "mtcsecond",
  "mtsmsnumber",
  "nbOccurrence",
  "pckSubPrePaidPackagesDataSlicesTmpl",
  "perioddays",
  "picture",
  "prepaidpackagetemplateid",
  "prepaidpackagetemplatename",
  "priority",
  "rdbDestinationZones",
  "rdbLocationZones",
  "recurring",
  "recurringPeriodicityFrequency",
  "recurringPeriodicityType",
  "reportUnitsPreviousPackage",
  "reseller",
  "resellerid",
  "sponsors",
  "throttleTillEnd",
  "throttlingActive",
  "throttlingErrorAction",
  "throttlingThreshold1Limit",
  "throttlingThreshold1Perc",
  "throttlingThreshold2Limit",
  "throttlingThreshold2Perc",
  "throttlingThreshold3Limit",
  "throttlingThreshold3Perc",
  "throttlingThreshold4Limit",
  "throttlingThreshold4Perc",
  "uiEndAvailibilityPeriod",
  "uiStartAvailablePeriod",
  "uiVisible",
  "userUiName",
] as const;

const PACKAGE_TEMPLATE_CREATE_FIELD_SET = new Set<string>(PACKAGE_TEMPLATE_CREATE_FIELDS);

/** Caller spellings that OCS rejects until they are renamed. Canonical key wins. */
export const PACKAGE_TEMPLATE_CREATE_ALIASES: Record<string, string> = {
  name: "prepaidpackagetemplatename",
  volume: "databyte",
  dataLimit: "databyte",
  dataBytes: "databyte",
  validityDays: "perioddays",
  periodDays: "perioddays",
  locationZoneId: "locationzoneid",
  destinationZoneId: "destinationzoneid",
  resellerId: "resellerid",
};

function templateRecord(
  input: Record<string, unknown> | string,
): { ok: true; body: Record<string, unknown> } | { ok: false; error: string } {
  if (typeof input !== "string") {
    if (input === null || typeof input !== "object" || Array.isArray(input)) {
      return { ok: false, error: "template must be a JSON object" };
    }
    return { ok: true, body: input };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch {
    return { ok: false, error: "template must be a JSON object" };
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, error: "template must be a JSON object" };
  }
  return { ok: true, body: parsed as Record<string, unknown> };
}

function templateName(raw: unknown): string | { error: string } {
  if (typeof raw !== "string") return { error: "prepaidpackagetemplatename must be a string" };
  const name = raw.trim();
  if (!name) return { error: "prepaidpackagetemplatename is required" };
  return name;
}

/**
 * Map a create-template body onto the fields `createPrepaidPackageTemplate` accepts.
 *
 * Does not invent an allowance. A missing data field stays missing; the MCP
 * tool runs {@link normalizePackageTemplate} first when it wants the unlimited
 * ceiling. `price` and `description` are refused. An explicit OCS key wins
 * over its alias.
 */
export function packageTemplateCreateParams(
  input: Record<string, unknown> | string,
): Record<string, unknown> | { error: string } {
  const parsed = templateRecord(input);
  if (!parsed.ok) return { error: parsed.error };
  const body = parsed.body;
  if ("price" in body) return { error: "createPrepaidPackageTemplate has no price field" };
  if ("description" in body) return { error: "createPrepaidPackageTemplate has no description field" };

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(body)) {
    if (PACKAGE_TEMPLATE_CREATE_FIELD_SET.has(key)) out[key] = value;
  }
  for (const [key, value] of Object.entries(body)) {
    const target = PACKAGE_TEMPLATE_CREATE_ALIASES[key];
    if (!target) {
      if (!PACKAGE_TEMPLATE_CREATE_FIELD_SET.has(key)) {
        return { error: `createPrepaidPackageTemplate does not accept ${key}` };
      }
      continue;
    }
    if (target in out) continue;
    if (target === "prepaidpackagetemplatename") {
      const name = templateName(value);
      if (typeof name !== "string") return name;
      out[target] = name;
      continue;
    }
    out[target] = value;
  }
  if ("prepaidpackagetemplatename" in out) {
    const name = templateName(out["prepaidpackagetemplatename"]);
    if (typeof name !== "string") return name;
    out["prepaidpackagetemplatename"] = name;
  }
  return out;
}

/**
 * MCP `create_package_template` entry. Normalizes the sold-plan shortcuts
 * (`unlimited`, `throttleAfterMb`, `throttleKbps`) first, then keeps only the
 * fields `createPrepaidPackageTemplate` accepts. The REST route must call
 * {@link packageTemplateCreateParams} directly so a name-only body does not
 * grow an unlimited allowance.
 */
export function packageTemplateCreateFromToolJson(
  template: string,
): Record<string, unknown> | { error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(template);
  } catch {
    return { error: "template must be a JSON object" };
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { error: "template must be a JSON object" };
  }
  let normalized: Record<string, unknown>;
  try {
    normalized = normalizePackageTemplate(parsed as Record<string, unknown>);
  } catch (err) {
    const message = err instanceof Error ? err.message : "template could not be normalized";
    return { error: message };
  }
  return packageTemplateCreateParams(normalized);
}

/**
 * `modifyPPTCore` accepts every create field plus `changeSponsor`.
 * `modifyPPTRecurring` and `modifyPPTThrottling` accept the create set only.
 */
export type PackageTemplateModifyMethod =
  | "modifyPPTCore"
  | "modifyPPTRecurring"
  | "modifyPPTThrottling";

const PACKAGE_TEMPLATE_MODIFY_EXTRA: Record<PackageTemplateModifyMethod, readonly string[]> = {
  modifyPPTCore: ["changeSponsor"],
  modifyPPTRecurring: [],
  modifyPPTThrottling: [],
};

/** Path identity. The handler sets `templateId` after this function returns. */
const PACKAGE_TEMPLATE_MODIFY_IDENTITY = new Set(["templateId", "prepaidpackagetemplateid"]);

export const PACKAGE_TEMPLATE_MODIFY_ALIASES: Record<string, string> = {
  ...PACKAGE_TEMPLATE_CREATE_ALIASES,
  periodicity: "recurringPeriodicityType",
  occurrences: "nbOccurrence",
  firstThresholdPercent: "throttlingThreshold1Perc",
  firstThresholdLimitKbps: "throttlingThreshold1Limit",
  errorAction: "throttlingErrorAction",
};

/** Accepted OCS names for one modifyPPT* method, before aliases. */
export function packageTemplateModifyFieldNames(
  method: PackageTemplateModifyMethod,
): readonly string[] {
  return [...PACKAGE_TEMPLATE_CREATE_FIELDS, ...PACKAGE_TEMPLATE_MODIFY_EXTRA[method]];
}

function changesRecord(
  input: Record<string, unknown> | string,
): { ok: true; body: Record<string, unknown> } | { ok: false; error: string } {
  if (typeof input !== "string") {
    if (input === null || typeof input !== "object" || Array.isArray(input)) {
      return { ok: false, error: "changes must be a JSON object" };
    }
    return { ok: true, body: input };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch {
    return { ok: false, error: "changes must be a JSON object" };
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, error: "changes must be a JSON object" };
  }
  return { ok: true, body: parsed as Record<string, unknown> };
}

function periodicityValue(raw: unknown): number | { error: string } {
  if (typeof raw === "number" && OCS_PERIODICITY_VALUES.includes(raw as never)) return raw;
  if (typeof raw === "string") {
    const key = raw.trim().toLowerCase();
    if (key === "daily" || key === "0") return OCS_PERIODICITY.DAILY;
    if (key === "weekly" || key === "1") return OCS_PERIODICITY.WEEKLY;
    if (key === "monthly" || key === "2") return OCS_PERIODICITY.MONTHLY;
  }
  return { error: "recurringPeriodicityType must be 0 (daily), 1 (weekly), or 2 (monthly)" };
}

function throttlingErrorActionValue(raw: unknown): number | { error: string } {
  if (typeof raw === "number" && Number.isInteger(raw)) return raw;
  if (typeof raw === "string" && /^-?\d+$/.test(raw.trim())) return Number(raw.trim());
  if (typeof raw === "string" && raw.trim().toLowerCase() === "continue_unthrottled") {
    return {
      error: "throttlingErrorAction is an integer. continue_unthrottled is not a modifyPPT value",
    };
  }
  return { error: "throttlingErrorAction is an integer" };
}

function applyTemplateField(
  out: Record<string, unknown>,
  target: string,
  value: unknown,
): { error: string } | undefined {
  if (target === "prepaidpackagetemplatename") {
    const name = templateName(value);
    if (typeof name !== "string") return name;
    out[target] = name;
    return undefined;
  }
  if (target === "recurringPeriodicityType") {
    const period = periodicityValue(value);
    if (typeof period !== "number") return period;
    out[target] = period;
    return undefined;
  }
  if (target === "throttlingErrorAction") {
    const action = throttlingErrorActionValue(value);
    if (typeof action !== "number") return action;
    out[target] = action;
    return undefined;
  }
  out[target] = value;
  return undefined;
}

/**
 * Map a partial template update onto the fields the modifyPPT* method accepts.
 *
 * Does not invent an allowance. `templateId` and `prepaidpackagetemplateid`
 * are dropped so the caller can set the path id last. `price` and
 * `description` are refused. `periodicity` daily/weekly/monthly becomes
 * `recurringPeriodicityType` 0/1/2. `continue_unthrottled` is refused:
 * `throttlingErrorAction` is an integer. An explicit OCS key wins over its alias.
 */
export function packageTemplateModifyParams(
  input: Record<string, unknown> | string,
  method: PackageTemplateModifyMethod,
): Record<string, unknown> | { error: string } {
  const parsed = changesRecord(input);
  if (!parsed.ok) return { error: parsed.error };
  const body = parsed.body;
  if ("price" in body) return { error: `${method} has no price field` };
  if ("description" in body) return { error: `${method} has no description field` };

  const accepted = new Set<string>([
    ...PACKAGE_TEMPLATE_CREATE_FIELDS,
    ...PACKAGE_TEMPLATE_MODIFY_EXTRA[method],
  ]);
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(body)) {
    if (PACKAGE_TEMPLATE_MODIFY_IDENTITY.has(key) || !accepted.has(key)) continue;
    const applied = applyTemplateField(out, key, value);
    if (applied) return applied;
  }
  for (const [key, value] of Object.entries(body)) {
    if (PACKAGE_TEMPLATE_MODIFY_IDENTITY.has(key)) continue;
    const target = PACKAGE_TEMPLATE_MODIFY_ALIASES[key];
    if (!target) {
      if (!accepted.has(key)) return { error: `${method} does not accept ${key}` };
      continue;
    }
    if (target in out) continue;
    const applied = applyTemplateField(out, target, value);
    if (applied) return applied;
  }
  if (Object.keys(out).length === 0) {
    return { error: "at least one template field required" };
  }
  return out;
}

/**
 * MCP modify-template entry. Core runs {@link normalizePackageTemplateChanges}
 * first so a zero allowance the caller sent becomes the unlimited ceiling, and
 * a rename stays a rename. Recurring and throttling do not.
 */
export function packageTemplateModifyFromToolJson(
  changes: string,
  method: PackageTemplateModifyMethod,
): Record<string, unknown> | { error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(changes);
  } catch {
    return { error: "changes must be a JSON object" };
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { error: "changes must be a JSON object" };
  }
  let body = parsed as Record<string, unknown>;
  if (method === "modifyPPTCore") {
    try {
      body = normalizePackageTemplateChanges(body);
    } catch (err) {
      const message = err instanceof Error ? err.message : "changes could not be normalized";
      return { error: message };
    }
  }
  return packageTemplateModifyParams(body, method);
}

/** Surfaces that must stay aligned on these builders. Used by the contract test. */
export const OCS_PARITY_SURFACES = ["api", "console", "mcp-stdio", "mcp-server", "cli"] as const;
